/**
 * 플랜 권한 판정 (PRD2 T-1.6, §6.2).
 * 서버가 생기기 전에는 이 함수가 화면의 정본이다. 로컬 플랜의 실제 역할은 소유자다.
 */

import { CAPABILITIES, CAPABILITY_DEFAULT_ON, planGrant, type CapabilityId, type Grant, type PlanRole } from "./matrix";

export const MEMBER_PLAN_ROLES = ["owner", "admin", "editor", "contributor", "viewer"] as const;
export type MemberPlanRole = (typeof MEMBER_PLAN_ROLES)[number];

/** done은 완료 후 회고를 고치는 상태다. 쓰기는 활성과 같다. */
export type PlanLifecycle = "active" | "done" | "archived" | "trashed";

export type PlanActor = {
  role: MemberPlanRole;
  capabilities: readonly CapabilityId[];
};

/** 각주 9·10·11. 기본은 모두 꺼져 있다. */
export type PlanAccessOptions = {
  recordConfirmByEditor?: boolean;
  viewerCanComment?: boolean;
  viewerCanDownload?: boolean;
};

export type PlanAccessResource = {
  status?: PlanLifecycle;
};

/** 아카이브 중에도 역할 판정이 허용이면 통과하는 읽기·복구·내보내기 (§12.4). */
const ARCHIVED_ALLOWED = new Set([
  "plan.meta.read",
  "plan.schedule.read",
  "plan.cost.read",
  "plan.audit.read",
  "plan.attachment.download",
  "plan.doc.read",
  "plan.export",
  "plan.export.localJson",
  "plan.unarchive",
]);

/** 휴지통에서는 메타와 복구만 남긴다. 콘텐츠 읽기는 404다 (§12.5). */
const TRASH_ALLOWED = new Set(["plan.meta.read", "plan.restore"]);

export function defaultCapabilities(role: MemberPlanRole): CapabilityId[] {
  return CAPABILITIES.filter((id) => (CAPABILITY_DEFAULT_ON[id] as readonly string[]).includes(role));
}

export function planActor(role: MemberPlanRole, capabilities?: readonly CapabilityId[]): PlanActor {
  return { role, capabilities: capabilities ?? defaultCapabilities(role) };
}

function grantAllows(grant: Grant, actor: PlanActor, options: PlanAccessOptions): boolean {
  if (grant.result === "deny") return false;
  if (grant.result === "allow") return true;
  if (grant.flag) return actor.capabilities.includes(grant.flag);
  if (grant.note === 8 || grant.note === 18) return true;
  if (grant.note === 9) return options.recordConfirmByEditor === true;
  if (grant.note === 10) return options.viewerCanComment === true;
  if (grant.note === 11) return options.viewerCanDownload === true;
  return false;
}

export function can(actor: PlanActor, actionId: string, resource?: PlanAccessResource, options: PlanAccessOptions = {}): boolean {
  const role = actor.role as PlanRole;
  const grant = planGrant(actionId, role);
  const status = resource?.status ?? "active";
  if (status === "trashed" && !TRASH_ALLOWED.has(actionId)) return false;
  if (status === "archived" && !ARCHIVED_ALLOWED.has(actionId)) return false;
  return grantAllows(grant, actor, options);
}
